import store from '@/store/BookingStore'
import { Booking, Traveller, isPartnerWebsiteSecured } from '../../../../requests-module'
import {
    computeAdultChildCounts,
    computeTravellerAges,
    getMatchingParticipantsFromLocalStorage,
    SavedParticipantData,
    splitTravellerIndicesByAdultChild,
} from './participantsLocalStorage'
import {
    hasWidgetPassengerIdentity,
    hasWidgetPassengersPrefill,
    widgetSlotToTravellerFields,
    type WidgetPassengerPrefill,
} from '@/logic/widgetMode/widgetPassengersPrefill'

const hasStrippedNames = (traveller: Traveller): boolean => {
    const firstName = (traveller.firstName || '').trim()
    const lastName = (traveller.lastName || '').trim()
    return !firstName && !lastName
}

const mergeTravellerFromSources = (
    apiTraveller: Traveller,
    storeTraveller?: Traveller,
    saved?: SavedParticipantData,
    widgetSlot?: WidgetPassengerPrefill,
): Traveller => {
    const widgetFields = widgetSlot ? widgetSlotToTravellerFields(widgetSlot) : null

    const firstName =
        widgetFields?.firstName?.trim() ||
        (storeTraveller?.firstName || '').trim() ||
        saved?.firstName ||
        apiTraveller.firstName ||
        null
    const lastName =
        widgetFields?.lastName?.trim() ||
        (storeTraveller?.lastName || '').trim() ||
        saved?.lastName ||
        apiTraveller.lastName ||
        null
    const gender =
        widgetFields?.gender ??
        storeTraveller?.gender ??
        saved?.gender ??
        apiTraveller.gender ??
        null
    const birthday =
        widgetFields?.birthday ||
        apiTraveller.birthday ||
        saved?.birthday ||
        storeTraveller?.birthday ||
        ''

    return {
        ...apiTraveller,
        firstName,
        lastName,
        gender,
        birthday,
        fulfilled: widgetFields?.fulfilled ?? !!(firstName && lastName && birthday),
    }
}

const mapSavedToCurrentByAdultChild = (
    travellers: Traveller[],
    currentAges: number[],
    savedTravellers: SavedParticipantData[],
    returnDate: string,
    storeTravellers: Traveller[],
    widgetList: WidgetPassengerPrefill[] | undefined,
): Traveller[] => {
    const savedAges = computeTravellerAges(
        savedTravellers.map((saved, index) => ({
            id: String(index),
            birthday: saved.birthday,
        })),
        returnDate,
    )

    const { adultIndices: currentAdults, childIndices: currentChildren } =
        splitTravellerIndicesByAdultChild(currentAges)
    const { adultIndices: savedAdults, childIndices: savedChildren } =
        splitTravellerIndicesByAdultChild(savedAges)

    return travellers.map((traveller, index) => {
        const widgetSlot = widgetList?.[index]
        if (widgetSlot && hasWidgetPassengerIdentity(widgetSlot)) {
            if (!hasStrippedNames(traveller)) {
                return mergeTravellerFromSources(traveller, traveller, undefined, widgetSlot)
            }
            const storeTraveller = storeTravellers.find((t) => t.id === traveller.id)
            return mergeTravellerFromSources(traveller, storeTraveller, undefined, widgetSlot)
        }

        if (!hasStrippedNames(traveller)) return traveller

        const storeTraveller = storeTravellers.find((t) => t.id === traveller.id)
        if (storeTraveller && ((storeTraveller.firstName || '').trim() || (storeTraveller.lastName || '').trim())) {
            return mergeTravellerFromSources(traveller, storeTraveller)
        }

        const adultSlot = currentAdults.indexOf(index)
        const childSlot = currentChildren.indexOf(index)
        let savedIdx: number | undefined
        if (adultSlot >= 0) savedIdx = savedAdults[adultSlot]
        else if (childSlot >= 0) savedIdx = savedChildren[childSlot]

        if (savedIdx === undefined) return traveller
        return mergeTravellerFromSources(traveller, undefined, savedTravellers[savedIdx])
    })
}

export const mergeParticipantsFromLocalStorageIntoBooking = (
    incoming: Booking,
    currentBooking?: Booking | null,
): Booking => {
    if (!incoming.website || !isPartnerWebsiteSecured(incoming.website)) {
        return incoming
    }
    if (!incoming.travellers?.length || !incoming.returnDate) {
        return incoming
    }

    const widgetPrefill = store.getters.getWidgetPassengersPrefill
    const useWidgetPrefill = hasWidgetPassengersPrefill(widgetPrefill)
    const widgetList = useWidgetPrefill ? widgetPrefill?.list : undefined

    const storeTravellers = currentBooking?.travellers ?? []
    const currentAges = computeTravellerAges(incoming.travellers, incoming.returnDate)
    const counts = computeAdultChildCounts(currentAges)
    const savedTravellers =
        !useWidgetPrefill && counts ? getMatchingParticipantsFromLocalStorage(counts) : null

    if (
        !savedTravellers &&
        !useWidgetPrefill &&
        !storeTravellers.some((t) => (t.firstName || '').trim() || (t.lastName || '').trim())
    ) {
        return incoming
    }

    const mergedTravellers = savedTravellers
        ? mapSavedToCurrentByAdultChild(
              incoming.travellers,
              currentAges,
              savedTravellers,
              incoming.returnDate,
              storeTravellers,
              widgetList,
          )
        : incoming.travellers.map((traveller, index) => {
              const widgetSlot = widgetList?.[index]
              if (!hasStrippedNames(traveller) && !widgetSlot) return traveller
              const storeTraveller = storeTravellers.find((t) => t.id === traveller.id)
              if (!storeTraveller && !widgetSlot) return traveller
              return mergeTravellerFromSources(traveller, storeTraveller, undefined, widgetSlot)
          })

    return { ...incoming, travellers: mergedTravellers }
}
